import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import styled from 'styled-components/macro';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  CircularProgress,
  makeStyles,
} from '@material-ui/core';
import {
  inboxesSelector,
  workdeskFilterSelector,
} from 'store/selectors';
import { useDispatch } from 'react-redux';
import { inboxesActions, workdeskFilterActions } from 'store/actions';
import { Inbox, Inboxes } from 'store/models/Inboxes';
import Tooltip from '@material-ui/core/Tooltip';
import gmailIcon from 'assets/images/gmail-logo.png';

const useStyles = makeStyles((theme) => ({
  avartar: {
    width: '40px',
    height: '40px',
    boxShadow: '0px 0px 3px 0.8px rgba(0, 0, 0, 0.25)',
    marginLeft: '9px',
    fontSize: '16px',
  },
}));
const InboxNav: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDispatch = useCallback(dispatch, []);
  const { isLoading, inboxes = [] } = useSelector(inboxesSelector);
  const inboxesLength = inboxes.length;
  const [isExpand, setIsExpand] = useState(false);
  const [isSelectedAll, setSelectedAll] = useState(true);
  const isSelectedAllRef = useRef(isSelectedAll);
  useEffect(() => {
    isSelectedAllRef.current = isSelectedAll;
  }, [isSelectedAll]);
  const { selectedInboxes, workdeskFilterInLsOnInit } = useSelector(
    workdeskFilterSelector,
  );
  const workdeskFilterInLsOnInitRef = useRef(
    workdeskFilterInLsOnInit,
  );

  useEffect(() => {
    workdeskFilterInLsOnInitRef.current = workdeskFilterInLsOnInit;
  }, [workdeskFilterInLsOnInit]);

  useEffect(() => {
    // to prevent reloading inboxes when click back from main
    if (!inboxesLength) {
      console.debug(`call getInboxes`);
      stableDispatch(inboxesActions.getInboxes());
    }
  }, [inboxesLength, stableDispatch]);

  const inboxesEmails = useMemo(
    () => inboxes.map((inbox) => inbox.address),
    [inboxes],
  );

  useEffect(() => {
    if (!inboxesEmails || !inboxesEmails.length) return;

    /* auto select all */
    console.debug(
      `workdeskFilterInLsOnInit`,
      workdeskFilterInLsOnInitRef.current,
      'isSelectedAll',
      isSelectedAllRef.current,
    );
    if (!workdeskFilterInLsOnInitRef.current) {
      stableDispatch(
        workdeskFilterActions.setSelectedInboxes(
          inboxesEmails,
          isSelectedAllRef.current,
        ),
      );
    }
  }, [inboxesEmails, stableDispatch]);

  useEffect(() => {
    console.debug(`inboxes: `, inboxes);
  }, [inboxes]);

  const handleToggleAll = () => {
    dispatch(
      workdeskFilterActions.setSelectedInboxes(
        inboxesEmails,
        !isSelectedAll,
      ),
    );
  };
  const handleSetIsExpand = (newValue: boolean) => {
    setIsExpand(newValue);
  };

  const handleOnToggleInbox = (inbox) => {
    dispatch(
      workdeskFilterActions.toggleSelectedInbox(inbox.address),
    );
  };
  /**
   * makes the selected all button's value depends on the selectedInboxes' value
   * check if any selected inboxes is false then change is selected all to false
   * if all is true, then change selected all to true
   */
  useEffect(() => {
    if (Object.values(selectedInboxes).length > 0) {
      // only consider when selectedInboxes has some length
      if (
        Object.values(selectedInboxes).some((selected) => !selected)
      ) {
        // if some is not selected
        setSelectedAll(false);
      } else if (
        Object.values(selectedInboxes).every((selected) => selected)
      ) {
        // if all is selected
        setSelectedAll(true);
      }
    }
  }, [selectedInboxes]);

  return (
    <SInboxNav
      data-testid="inbox-nav"
      className={isExpand ? 'expand' : 'collapse'}
      onMouseEnter={() => handleSetIsExpand(true)}
      onMouseLeave={() => handleSetIsExpand(false)}
    >
      {isLoading ? (
        <Box textAlign="center" width="100%" marginTop="0.5rem">
          <CircularProgress size="25px" />
        </Box>
      ) : (
        <SInboxList>
          <SButtonAll
            className={`clickable ${
              isSelectedAll ? 'is-selected' : 'not-selected'
            } ${isExpand ? 'expand' : 'collapsed'}`}
            onClick={() => handleToggleAll()}
          >
            {/* TODO: consider refactoring this */}
            {isSelectedAll ? (
              <SText
                className={`${isExpand ? 'expand' : ''} Deselect`}
              >
                Deselect {isExpand && 'all'}
              </SText>
            ) : (
              <SText className={`${isExpand ? 'expand' : ''} Select`}>
                Select {isExpand && 'all'}
              </SText>
            )}
          </SButtonAll>
          {inboxes.map((a) => {
            const isSelected = Inboxes.isInboxSelected(
              selectedInboxes,
              a,
            );
            return (
              <SButtonBase
                key={a.address}
                data-testid={`inbox-nav-button_${a.address}`}
                className={`${
                  isSelected ? 'is-selected' : 'not-selected'
                } ${isExpand ? 'expand' : 'collapsed'} clickable`}
                onClick={() => handleOnToggleInbox(a)}
              >
                <Avatar
                  style={{ backgroundColor: `${a.color}` }}
                  src={a.imageUrl || undefined}
                  title={a.address}
                  className={`${classes.avartar}`}
                >
                  {new Inbox(a).getLetterToShowInAvatar()}
                </Avatar>
                <Tooltip
                  title={<STooltipText>{a.address}</STooltipText>}
                >
                  <SFullEmail>{a.address}</SFullEmail>
                </Tooltip>
                {a.url && (
                  <SGoToInboxButton
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                  >
                    <img src={gmailIcon} alt="go to inbox" />
                  </SGoToInboxButton>
                )}
              </SButtonBase>
            );
          })}
        </SInboxList>
      )}
    </SInboxNav>
  );
};
export default InboxNav;
const STooltipText = styled.div`
  font-size: 13px;
`;

const SText = styled.div`
  display: flex;
  font-weight: bold;
  font-size: ${(props) => props.theme.typography.fontSize.regular};
  &.deselect {
    margin-left: 4px;
  }
  &.select {
    margin-left: 8px;
  }
`;
const SFullEmail = styled.div`
  margin-left: 1rem;
  text-align: left;
  line-height: 20px;
  font-size: ${(props) => props.theme.typography.fontSize.title};
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  // cannot use display flex align item center because ... will not show
  margin-top: auto;
  margin-bottom: auto;
`;

const SButtonBase = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  &.is-selected {
    border-left: 5px solid
      ${(props) => props.theme.palette.primary.main};
    background: ${(props) => props.theme.palette.grey[300]};
  }
  &.not-selected {
    margin-left: 5px;
  }
  &.expand {
    padding: 5px;
    width: 95%;
  }
  &.collapsed {
    padding: 5px;
    &.not-selected {
      margin-left: 5px;
    }
  }
`;
const SButtonAll = styled.div`
  line-height: ${(props) => props.theme.typography.lineHeight.body};

  height: 50px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  color: ${(props) => props.theme.palette.primary.main};
  &.is-selected {
    border-left: 5px solid
      ${(props) => props.theme.palette.primary.main};
    background-color: ${(props) => props.theme.palette.grey[300]};
  }
  &.not-selected {
    margin-left: 5px;
  }
  &.expand {
    font-weight: bold;
    width: 100%;
  }
  &.collapsed {
    &.not-selected {
      margin-left: 5px;
    }
  }
`;
const SInboxList = styled.div`
  width: 100%;
  padding-top: 1px;
  padding-bottom: 5rem;
  overflow-y: scroll;
  overflow-x: hidden;
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;
const SInboxNav = styled.div`
  height: 100%;
  background-color: ${(props) =>
    props.theme.palette.background.paper};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  display: flex;
  width: 5rem;
  transition: width 0.5s;
  position: fixed; // to be above/bottom the back to top button of the TicketGroupTable
  right: 0; // to be above/bottom the back to top button of the TicketGroupTable
  z-index: 0;
  &.expand {
    width: 20rem;
    z-index: 1; // to be above the back to top button of the TicketGroupTable
  }
`;

const SGoToInboxButton = styled.a`
  padding: 0.5rem;
  text-decoration: inherit;
  color: inherit;
  &:visited {
    text-decoration: inherit;
    color: inherit;
  }
  & img {
    width: 30px;
  }
  &:hover {
    transform: scale(1.1);
  }
  transition: transform 0.15s;
`;
